import {
  CONFIG,
  SEARCH_INDEXES,
} from '@bulknutrients/bulk-shared-lib/constants';
import type { PopularSearch } from '@bulknutrients/bulk-shared-lib/interfaces/IPopularSearch';
import { client } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { Button } from '@components/Button';
import { Input } from '@components/FormComponents';
import { Icons } from '@components/Icons/Icons';
import { ProductCardAutocompleteSearch } from '@components/ProductCard/ProductCard';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useSearch } from '@hooks/useSearch';
import { useRouter } from 'next/router';
import { type ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import useSWR from 'swr';
import { SearchResultsLinkList } from './SearchResultsLinkList';

function PopularSearches(): ReactElement {
  const fetcher = async (url) => {
    const query = `//groq
      *[_type == "popularSearches" && !(_id in path("drafts.**"))]
      {
        links[]{
          ...,
          "noFollow": coalesce(noFollow, false),
          internalLink->{slug, title}
        }
      }
    `;

    const result = await client.fetch(query);
    if (result && result.length > 0) {
      // array always have a single item because links is the only child of popularSearches in Sanity
      return result[0].links;
    } else {
      const error = new Error('Unable to fetch popular searches');
      throw error;
    }
  };

  const { data: links } = useSWR<PopularSearch[] | null>(
    '/api/popularSearches',
    fetcher,
    {
      refreshInterval: 3600000, // 60 minutes
      revalidateOnFocus: false,
    }
  );

  return links ? (
    <div>
      <h4 className="m-0 mb-2 font-secondary text-2xl font-bold uppercase text-black dark:text-grey-light w-full border-b">
        Popular Searches
      </h4>
      <ul>
        {links.map((linkEl) => {
          const url = `${
            linkEl.internalLink
              ? linkEl.internalLink?.slug.current
              : linkEl.link
          }`;
          return (
            <li key={url}>
              <TextLink href={url} rel={linkEl.noFollow ? 'nofollow' : ''}>
                {linkEl.label}
              </TextLink>
            </li>
          );
        })}
      </ul>
    </div>
  ) : (
    <></>
  );
}

function Modal({
  close,
  onSubmit,
}: {
  close: () => void;
  onSubmit: (data: any, e?: React.BaseSyntheticEvent) => void;
}): ReactElement {
  const { watch, handleSubmit } = useFormContext();
  const query = watch('query');

  const { data } = useSearch({
    index: '*',
    query: query,
    pageSize: CONFIG.autocompleteProducts,
  });

  const productHits =
    data?.find((d) => d.index === SEARCH_INDEXES.PRODUCTS)?.nbHits ?? 0;
  const pageHits =
    data?.find((d) => d.index === SEARCH_INDEXES.PAGES)?.nbHits ?? 0;
  const categoryHits =
    data?.find((d) => d.index === SEARCH_INDEXES.CATEGORIES)?.nbHits ?? 0;
  const faqHits =
    data?.find((d) => d.index === SEARCH_INDEXES.FAQS)?.nbHits ?? 0;
  const blogHits =
    data?.find((d) => d.index === SEARCH_INDEXES.BLOGS)?.nbHits ?? 0;

  const HeadingStyles =
    'flex gap-4 m-0 mb-2 font-secondary text-2xl font-bold uppercase text-black dark:text-grey-light w-full border-b';

  // the top position matches the height of the InfoNavbar
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      id="autocomplete-modal"
      className="no-scroll absolute left-0 right-0 top-7 bottom-0 w-screen h-[calc(100dvh-28px)] py-6 px-10 sm:px-15 z-3 my-0 cursor-auto overflow-auto bg-white text-grey-darkest opacity-100 transition-all delay-200 duration-150 ease-in-out dark:bg-black dark:text-white overscroll-contain"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className="absolute right-6 top-6 border-none bg-transparent font-secondary text-4xl text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:no-underline dark:text-white dark:hover:cursor-pointer dark:hover:text-orange dark:hover:no-underline dark:focus:cursor-pointer dark:focus:no-underline print:hidden"
      >
        <Icons.Close />
      </button>
      <div className="max-w-max-width mx-auto pr-10 sm:pr-0">
        <Input
          name="query"
          type="search"
          label=""
          className="w-full"
          autoFocus
        />
      </div>
      <div className="mt-4 grid gap-10 content-start grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 max-w-max-width mx-auto">
        {query ? (
          <>
            {/* Products */}
            <div id="products" className="col-span-full">
              <div className="flex flex-row items-baseline">
                <h4 className={HeadingStyles}>
                  Products{' '}
                  <TextLink
                    href={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.PRODUCTS}`}
                    as={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.PRODUCTS}`}
                    data-umami-event-slug={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.PRODUCTS}`}
                    data-umami-event-location="Search Autocomplete Products Link"
                    className="text-xl"
                  >
                    View {productHits} product{productHits > 1 && 's'}
                  </TextLink>
                </h4>
              </div>

              {productHits === 0 ? (
                <p>No products found</p>
              ) : (
                <div className="ga-autocomplete ga-autocomplete-products grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-10">
                  {(
                    data?.find((d) => d.index === SEARCH_INDEXES.PRODUCTS)
                      ?.hits ?? []
                  ).map((item) => (
                    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: using a div here to prevent the click event from bubbling up to the parent
                    <div
                      key={item.entityId}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ProductCardAutocompleteSearch
                        product={item}
                        location="Autocomplete Search"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pages */}
            <div>
              <h4 className={HeadingStyles}>
                Pages{' '}
                <TextLink
                  href={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.PAGES}`}
                  as={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.PAGES}`}
                  data-umami-event-slug={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.PAGES}`}
                  data-umami-event-location="Search Autocomplete Pages Link"
                  className="text-xl"
                >
                  View {pageHits} page{pageHits > 1 && 's'}
                </TextLink>
              </h4>
              <SearchResultsLinkList
                items={
                  data
                    ? [
                        ...(data.find((el) => el.index === SEARCH_INDEXES.PAGES)
                          ?.hits ?? []),
                        ...(data
                          .find((el) => el.index === SEARCH_INDEXES.AMBASSADORS)
                          ?.hits?.map((a) => ({
                            ...a,
                            title: `${a.name} (Ambassador)`,
                          })) ?? []),
                        ...(data
                          .find((el) => el.index === SEARCH_INDEXES.AUTHORS)
                          ?.hits?.map((a) => ({
                            ...a,
                            title: `${a.name} (Expert)`,
                          })) ?? []),
                      ]
                    : []
                }
                noResultsMessage="No pages found"
              />
            </div>

            {/* Categories */}
            <div>
              <h4 className={HeadingStyles}>
                Categories{' '}
                <TextLink
                  href={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.CATEGORIES}`}
                  as={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.CATEGORIES}`}
                  data-umami-event-slug={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.CATEGORIES}`}
                  data-umami-event-location="Search Autocomplete Categories Link"
                  className="text-xl"
                >
                  View {categoryHits} categor{categoryHits > 1 ? 'ies' : 'y'}
                </TextLink>
              </h4>
              <SearchResultsLinkList
                items={
                  data
                    ? (
                        data.find(
                          (el) => el.index === SEARCH_INDEXES.CATEGORIES
                        )?.hits ?? []
                      ).map((c) => ({
                        ...c,
                        title: c.parent ? `${c.parent} > ${c.title}` : c.title,
                      }))
                    : []
                }
                noResultsMessage="No categories found"
              />
            </div>

            {/* FAQs */}
            <div>
              <h4 className={HeadingStyles}>
                FAQs{' '}
                <TextLink
                  href={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.FAQS}`}
                  as={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.FAQS}`}
                  data-umami-event-slug={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.FAQS}`}
                  data-umami-event-location="Search Autocomplete FAQs Link"
                  className="text-xl"
                >
                  View {faqHits} FAQs
                </TextLink>
              </h4>
              <SearchResultsLinkList
                items={
                  data
                    ? (
                        data.find((el) => el.index === SEARCH_INDEXES.FAQS)
                          ?.hits ?? []
                      ).map((c) => ({
                        ...c,
                        title: c.parent ? `${c.parent} > ${c.title}` : c.title,
                      }))
                    : []
                }
                noResultsMessage="No FAQs found"
              />
            </div>

            {/* Blogs */}
            <div>
              <h4 className={HeadingStyles}>
                Blogs{' '}
                <TextLink
                  href={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.BLOGS}`}
                  as={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.BLOGS}`}
                  data-umami-event-slug={`/search-results-page?q=${encodeURIComponent(query)}&index=${SEARCH_INDEXES.BLOGS}`}
                  data-umami-event-location="Search Autocomplete Blogs Link"
                  className="text-xl"
                >
                  View {blogHits} Blogs
                </TextLink>
              </h4>
              <SearchResultsLinkList
                items={
                  data
                    ? (
                        data.find((el) => el.index === SEARCH_INDEXES.BLOGS)
                          ?.hits ?? []
                      ).map((c) => ({
                        ...c,
                        title: c.parent ? `${c.parent} > ${c.title}` : c.title,
                      }))
                    : []
                }
                noResultsMessage="No blogs found"
              />
            </div>
          </>
        ) : (
          <PopularSearches />
        )}
      </div>
      {query && (
        <TextLink
          href={`/search-results-page?q=${encodeURIComponent(query)}`}
          as={`/search-results-page?q=${encodeURIComponent(query)}`}
          data-umami-event-slug={`/search-results-page?q=${encodeURIComponent(query)}`}
          data-umami-event-location="Search Autocomplete View All Link"
          className="text-xl max-w-max-width mx-auto my-10 block"
        >
          View all results for "{query}"
        </TextLink>
      )}
    </form>
  );
}

export function Autocomplete(): ReactElement {
  const [open, setOpen] = useState(false);

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
  });
  const router = useRouter();

  const query = rhfMethods.watch('query');

  const handleFocus = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (query) {
      setOpen(true);
    }
  }, [query]);

  // close the search modal on route change
  useEffect(() => {
    const handleRouteChange = () => {
      setOpen(false);
      rhfMethods.resetField('query');
    };

    router.events.on('routeChangeStart', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  // Close on click outside
  useEffect(() => {
    const clickListener = (e) => {
      if (
        !e.target.closest('#autocomplete-modal') &&
        !e.target.closest('input[name="query"]') &&
        !e.target.closest('button[aria-label="Open Search"]') &&
        !e.target.closest('form') &&
        !e.target.closest('.mx-auto.md\\:order-2')
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('click', clickListener);

    return () => {
      document.removeEventListener('click', clickListener);
    };
  }, []);

  // Close on escape
  useEffect(() => {
    const escapeListener = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', escapeListener);

    return () => {
      document.removeEventListener('keydown', escapeListener);
    };
  }, []);

  const onSubmit = (data, e) => {
    e?.preventDefault();
    const q = (data?.query ?? rhfMethods.getValues().query ?? '').trim();
    setOpen(false);
    if (q) {
      router.push(`/search-results-page?q=${encodeURIComponent(q)}`);
    }
  };

  return (
    <FormProvider {...rhfMethods}>
      <div>
        <Button
          type="button"
          buttonStyle="tertiary"
          onClick={handleFocus}
          icon={<Icons.Search size="1.75rem" />}
          aria-label="Open Search"
          className="md:hidden"
        />

        <form
          onSubmit={rhfMethods.handleSubmit(onSubmit)}
          className="hidden md:block w-fit"
        >
          <Input
            name="query"
            type="search"
            label=""
            onFocus={handleFocus}
            className="w-xs transition-all duration-200 ease-in-out"
          />
        </form>
        {open && <Modal close={() => setOpen(false)} onSubmit={onSubmit} />}
      </div>
    </FormProvider>
  );
}
